import { useCallback } from "react";
import { looksTechnical } from "../../../lib/technical-text";
import { useI18n } from "@wato/i18n/react";
import type { MessageKey } from "@wato/i18n";
import type { AiAgentTypeView } from "../../../api/automation-types";
import type { ChannelSummary } from "../../../api/types";

/** Display label of an agent's scope (`null` = no scope chosen yet). */
export const AGENT_TYPE_KEYS: Readonly<Record<AiAgentTypeView | "none", MessageKey>> = {
  support_faq: "dashboard.aiAgents.types.support_faq",
  sales_assistant: "dashboard.aiAgents.types.sales_assistant",
  order_tracking: "dashboard.aiAgents.types.order_tracking",
  lead_qualification: "dashboard.aiAgents.types.lead_qualification",
  prospector_outreach: "dashboard.aiAgents.types.prospector_outreach",
  none: "dashboard.aiAgents.types.none",
};

/** Scopes a blank agent can be given (every agent has one: there is no catch-all agent). */
export const AGENT_TYPES: readonly AiAgentTypeView[] = [
  "support_faq",
  "sales_assistant",
  "order_tracking",
  "lead_qualification",
];

/** "Bound to this agent" test for the channels list. */
export const isBoundTo = (channel: ChannelSummary, agentId: string): boolean =>
  channel.aiAgentId === agentId;

/** Channel name for lists and pickers: its own name, phone or reference plus the kind. */
export function useChannelLabel(): (channel: ChannelSummary) => string {
  const { t } = useI18n();
  return useCallback(
    (channel) => {
      const kind =
        channel.provider === "whatsapp_waha"
          ? t("dashboard.aiAgents.channel.qr")
          : channel.provider === "whatsapp_cloud"
            ? t("dashboard.aiAgents.channel.cloud")
            : channel.provider.startsWith("instagram")
              ? t("dashboard.aiAgents.channel.instagram")
              : t("dashboard.aiAgents.channel.other");
      // FB3-46: QR session ids (`ws-…-ch-…`) and UUIDs are never a customer-facing name.
      const name =
        [
          channel.displayName,
          channel.displayHandle ? `@${channel.displayHandle}` : null,
          channel.displayPhoneNumber,
          channel.externalRef,
        ].find(
          (n): n is string =>
            typeof n === "string" &&
            n !== "" &&
            !looksTechnical(n) &&
            // A bare platform id (e.g. an Instagram account id) is never a name.
            !(n === channel.externalRef && /^\d{12,}$/u.test(n)),
        ) ?? "";
      return name ? t("dashboard.aiAgents.channel.option", { name, kind }) : kind;
    },
    [t],
  );
}
